import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

/** El color dice de qué límite se trata antes de que se lea el título. */
export type UpsellTone = "amber" | "blue" | "emerald" | "violet";

const TONES: Record<
  UpsellTone,
  { rule: string; badge: string; primary: string }
> = {
  amber: {
    rule: "from-[#F0A83A] to-[#0B4FD6]",
    badge: "bg-[#FEF4E6] text-[#B5741A]",
    primary: "bg-[#0B4FD6] hover:bg-[#0940A8]",
  },
  blue: {
    rule: "from-[#0B4FD6] to-[#17B667]",
    badge: "bg-[#EAF1FF] text-[#0B4FD6]",
    primary: "bg-[#0B4FD6] hover:bg-[#0940A8]",
  },
  emerald: {
    rule: "from-[#17B667] to-[#0B4FD6]",
    badge: "bg-[#E7F7EE] text-[#0E9A55]",
    primary: "bg-[#0B4FD6] hover:bg-[#0940A8]",
  },
  violet: {
    rule: "from-[#8B5CF6] to-[#0B4FD6]",
    badge: "bg-[#F3EBFF] text-[#7C3AED]",
    primary: "bg-[#8B5CF6] hover:bg-[#7C3AED]",
  },
};

/**
 * Marco de los modales que ofrecen mejorar el plan: filete degradado, ilustración,
 * píldora de estado y un par de acciones apiladas. Todos comparten esta forma para
 * que topar con un límite se lea siempre igual, venga de donde venga.
 */
export function UpsellModal({
  open,
  onOpenChange,
  tone,
  badge,
  title,
  description,
  illustration,
  children,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tone: UpsellTone;
  badge: string;
  title: string;
  description: ReactNode;
  illustration: ReactNode;
  /** Bloque opcional entre el texto y las acciones (un ejemplo, un dato). */
  children?: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel: string;
  /** Por defecto la salida solo cierra: ningún límite se resuelve quedándose aquí. */
  onSecondary?: () => void;
}) {
  const styles = TONES[tone];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 overflow-hidden rounded-[20px] bg-white p-0 sm:max-w-[440px]"
      >
        <div className={cn("h-1 bg-gradient-to-r", styles.rule)} />

        <div className="flex justify-center px-[38px] pt-9">{illustration}</div>

        <div className="px-[38px] pt-[22px] text-center">
          <span
            className={cn(
              "inline-flex rounded-full px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.08em]",
              styles.badge,
            )}
          >
            {badge}
          </span>
          <DialogTitle className="mt-3.5 text-[23px] font-extrabold leading-[1.25] tracking-[-0.02em] text-[#0F172A]">
            {title}
          </DialogTitle>
          <p className="mt-2.5 text-[15px] leading-[1.6] text-[#5B6472]">
            {description}
          </p>
        </div>

        {children && <div className="px-[38px] pt-5">{children}</div>}

        <div className="px-[38px] pb-[34px] pt-[26px]">
          <button
            type="button"
            onClick={onPrimary}
            className={cn(
              "w-full rounded-full px-7 py-[15px] text-[15px] font-bold text-white transition-colors",
              styles.primary,
            )}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={() => (onSecondary ? onSecondary() : onOpenChange(false))}
            className="mt-2.5 w-full px-7 py-2.5 text-[14px] font-bold text-[#8A93A6] transition-colors hover:text-[#5B6472]"
          >
            {secondaryLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
